export type AchievementType = 'primer_seguidor' | 'primer_saludo' | 'primera_cancion' | 'primer_concurso' | 'primer_evento' | 'primer_club';
export type AchievementLevel = 'bronce' | 'plata' | 'oro' | 'platino' | 'diamante';

export interface Achievement {
  id: string;
  type: AchievementType;
  name: string;
  description: string;
  icon: string;
  emoji: string;
  color: string;
  bgColor: string;
  pointsReward: number;
  progressRequired: number;
  listenerProgress: number;
  unlocked: boolean;
  unlockedDate?: string;
  repeatable: boolean;
  repeatCount: number;
  tier: 'basico' | 'intermedio' | 'avanzado' | 'maestro';
}

export interface LevelConfig {
  level: AchievementLevel;
  name: string;
  emoji: string;
  icon: string;
  color: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  minAchievements: number;
  minScore: number;
  benefits: string[];
  badge: string;
}

export interface ListenerAchievementProfile {
  listenerName: string;
  listenerAvatar: string;
  currentLevel: AchievementLevel;
  currentLevelProgress: number;
  totalAchievements: number;
  totalScore: number;
  unlockedAchievements: string[];
  pendingAchievements: string[];
  nextLevel: AchievementLevel | null;
  nextLevelNeeded: number;
  globalRank: number;
  totalListeners: number;
}

export interface AchievementScoreAction {
  action: string;
  points: number;
  icon: string;
  color: string;
  description: string;
}

export const achievementTypeLabels: Record<AchievementType, string> = {
  primer_seguidor: 'Primer Seguidor',
  primer_saludo: 'Primer Saludo',
  primera_cancion: 'Primera Canción',
  primer_concurso: 'Primer Concurso',
  primer_evento: 'Primer Evento',
  primer_club: 'Primer Club',
};

export const achievementTypeIcons: Record<AchievementType, string> = {
  primer_seguidor: 'ri-user-add-line',
  primer_saludo: 'ri-chat-smile-2-line',
  primera_cancion: 'ri-music-line',
  primer_concurso: 'ri-trophy-line',
  primer_evento: 'ri-calendar-event-line',
  primer_club: 'ri-shield-star-line',
};

export const achievementTypeColors: Record<AchievementType, { color: string; bgColor: string; textColor: string }> = {
  primer_seguidor: { color: 'bg-pink-50 text-pink-700 border-pink-200', bgColor: 'bg-pink-50', textColor: 'text-pink-700' },
  primer_saludo: { color: 'bg-teal-50 text-teal-700 border-teal-200', bgColor: 'bg-teal-50', textColor: 'text-teal-700' },
  primera_cancion: { color: 'bg-amber-50 text-amber-700 border-amber-200', bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
  primer_concurso: { color: 'bg-orange-50 text-orange-700 border-orange-200', bgColor: 'bg-orange-50', textColor: 'text-orange-700' },
  primer_evento: { color: 'bg-cyan-50 text-cyan-700 border-cyan-200', bgColor: 'bg-cyan-50', textColor: 'text-cyan-700' },
  primer_club: { color: 'bg-violet-50 text-violet-700 border-violet-200', bgColor: 'bg-violet-50', textColor: 'text-violet-700' },
};

export const levelConfigs: LevelConfig[] = [
  {
    level: 'bronce',
    name: 'Bronce',
    emoji: '🥉',
    icon: 'ri-medal-line',
    color: 'bg-amber-700',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-800',
    borderColor: 'border-amber-300',
    minAchievements: 0,
    minScore: 0,
    benefits: ['Perfil básico', 'Acceso a rankings'],
    badge: 'Nuevo Oyente',
  },
  {
    level: 'plata',
    name: 'Plata',
    emoji: '🥈',
    icon: 'ri-medal-line',
    color: 'bg-slate-400',
    bgColor: 'bg-slate-50',
    textColor: 'text-slate-700',
    borderColor: 'border-slate-300',
    minAchievements: 2,
    minScore: 250,
    benefits: ['Insignia de Plata', 'Aparición en búsquedas', 'Club Oficial'],
    badge: 'Oyente Plata',
  },
  {
    level: 'oro',
    name: 'Oro',
    emoji: '🥇',
    icon: 'ri-medal-line',
    color: 'bg-yellow-500',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-800',
    borderColor: 'border-yellow-300',
    minAchievements: 4,
    minScore: 600,
    benefits: ['Insignia de Oro', 'Sección Destacados', 'Estadísticas avanzadas', 'Badge verificado'],
    badge: 'Oyente Oro',
  },
  {
    level: 'platino',
    name: 'Platino',
    emoji: '💎',
    icon: 'ri-award-line',
    color: 'bg-cyan-500',
    bgColor: 'bg-cyan-50',
    textColor: 'text-cyan-800',
    borderColor: 'border-cyan-300',
    minAchievements: 6,
    minScore: 1200,
    benefits: ['Insignia de Platino', 'Prioridad en eventos', 'Acceso anticipado', 'Reconocimiento en vivo', 'Newsletter VIP'],
    badge: 'Oyente Platino',
  },
  {
    level: 'diamante',
    name: 'Diamante',
    emoji: '👑',
    icon: 'ri-vip-crown-line',
    color: 'bg-gradient-to-r from-indigo-500 to-pink-500',
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-800',
    borderColor: 'border-indigo-300',
    minAchievements: 10,
    minScore: 2500,
    benefits: ['Insignia de Diamante', 'Hall of Fame', 'Máxima exposición', 'Soporte 24/7', 'Features beta', 'Entrevista mensual', 'Revenue Share'],
    badge: 'Leyenda RFW',
  },
];

export const allAchievements: Achievement[] = [
  // ═══ TIER BÁSICO ═══
  {
    id: 'ach-001',
    type: 'primer_seguidor',
    name: 'Primer Seguidor',
    description: 'Conseguí tu primer seguidor en RFW. Alguien eligió estar conectado con vos.',
    icon: 'ri-user-add-line',
    emoji: '👤',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    pointsReward: 150,
    progressRequired: 1,
    listenerProgress: 1,
    unlocked: true,
    unlockedDate: '2026-03-12T10:30:00Z',
    repeatable: false,
    repeatCount: 0,
    tier: 'basico',
  },
  {
    id: 'ach-002',
    type: 'primer_saludo',
    name: 'Primer Saludo',
    description: 'Enviaste tu primer saludo a una radio. Rompiste el hielo con tu comunidad.',
    icon: 'ri-chat-smile-2-line',
    emoji: '👋',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    pointsReward: 100,
    progressRequired: 1,
    listenerProgress: 1,
    unlocked: true,
    unlockedDate: '2026-03-15T14:20:00Z',
    repeatable: false,
    repeatCount: 0,
    tier: 'basico',
  },
  {
    id: 'ach-003',
    type: 'primera_cancion',
    name: 'Primera Canción',
    description: 'Pediste tu primera canción al aire. La banda sonora de tu vida ahora suena en RFW.',
    icon: 'ri-music-line',
    emoji: '🎵',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    pointsReward: 100,
    progressRequired: 1,
    listenerProgress: 1,
    unlocked: true,
    unlockedDate: '2026-04-02T19:45:00Z',
    repeatable: false,
    repeatCount: 0,
    tier: 'basico',
  },
  {
    id: 'ach-004',
    type: 'primer_concurso',
    name: 'Primer Concurso',
    description: 'Participaste en tu primer concurso RFW. La competencia empieza acá.',
    icon: 'ri-trophy-line',
    emoji: '🏆',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    pointsReward: 200,
    progressRequired: 1,
    listenerProgress: 1,
    unlocked: true,
    unlockedDate: '2026-04-18T16:00:00Z',
    repeatable: false,
    repeatCount: 0,
    tier: 'basico',
  },
  {
    id: 'ach-005',
    type: 'primer_evento',
    name: 'Primer Evento',
    description: 'Asististe a tu primer evento RFW. La comunidad se vive en persona.',
    icon: 'ri-calendar-event-line',
    emoji: '🎪',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    pointsReward: 200,
    progressRequired: 1,
    listenerProgress: 0,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'basico',
  },
  {
    id: 'ach-006',
    type: 'primer_club',
    name: 'Primer Club',
    description: 'Te uniste a tu primer club de fans. Ahora sos parte de una familia.',
    icon: 'ri-shield-star-line',
    emoji: '🛡️',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    pointsReward: 250,
    progressRequired: 1,
    listenerProgress: 1,
    unlocked: true,
    unlockedDate: '2026-05-08T11:15:00Z',
    repeatable: false,
    repeatCount: 0,
    tier: 'basico',
  },

  // ═══ TIER INTERMEDIO ═══
  {
    id: 'ach-007',
    type: 'primer_seguidor',
    name: '10 Seguidores',
    description: 'Alcanzaste los 10 seguidores. Tu comunidad empieza a crecer.',
    icon: 'ri-user-add-line',
    emoji: '👥',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    pointsReward: 300,
    progressRequired: 10,
    listenerProgress: 7,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'intermedio',
  },
  {
    id: 'ach-008',
    type: 'primer_saludo',
    name: '5 Saludos Enviados',
    description: 'Enviaste 5 saludos a distintas radios. Sos un oyente activo.',
    icon: 'ri-chat-smile-2-line',
    emoji: '💬',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    pointsReward: 250,
    progressRequired: 5,
    listenerProgress: 4,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'intermedio',
  },
  {
    id: 'ach-009',
    type: 'primera_cancion',
    name: '5 Canciones Pedidas',
    description: 'Pediste 5 canciones. Tenés buen gusto musical.',
    icon: 'ri-music-line',
    emoji: '🎶',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    pointsReward: 250,
    progressRequired: 5,
    listenerProgress: 3,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'intermedio',
  },
  {
    id: 'ach-010',
    type: 'primer_concurso',
    name: '3 Concursos',
    description: 'Participaste en 3 concursos. La suerte favorece a los que intentan.',
    icon: 'ri-trophy-line',
    emoji: '🎯',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    pointsReward: 400,
    progressRequired: 3,
    listenerProgress: 2,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'intermedio',
  },
  {
    id: 'ach-011',
    type: 'primer_evento',
    name: '3 Eventos',
    description: 'Asististe a 3 eventos RFW. Sos parte activa de la comunidad.',
    icon: 'ri-calendar-event-line',
    emoji: '📅',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    pointsReward: 400,
    progressRequired: 3,
    listenerProgress: 0,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'intermedio',
  },
  {
    id: 'ach-012',
    type: 'primer_club',
    name: 'Miembro Activo de Club',
    description: 'Participaste en 5 actividades de tu club. El compromiso se nota.',
    icon: 'ri-shield-star-line',
    emoji: '⚔️',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    pointsReward: 500,
    progressRequired: 5,
    listenerProgress: 2,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'intermedio',
  },

  // ═══ TIER AVANZADO ═══
  {
    id: 'ach-013',
    type: 'primer_seguidor',
    name: '50 Seguidores',
    description: 'Alcanzaste los 50 seguidores. Tu influencia crece.',
    icon: 'ri-user-add-line',
    emoji: '🌟',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    pointsReward: 600,
    progressRequired: 50,
    listenerProgress: 17,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'avanzado',
  },
  {
    id: 'ach-014',
    type: 'primer_saludo',
    name: 'Saludo Leído al Aire',
    description: 'Uno de tus saludos fue leído al aire por un locutor. ¡Fama!',
    icon: 'ri-chat-smile-2-line',
    emoji: '📢',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    pointsReward: 500,
    progressRequired: 1,
    listenerProgress: 1,
    unlocked: true,
    unlockedDate: '2026-04-20T10:30:00Z',
    repeatable: false,
    repeatCount: 0,
    tier: 'avanzado',
  },
  {
    id: 'ach-015',
    type: 'primera_cancion',
    name: 'Canción Dedicada al Aire',
    description: 'Una canción que pediste fue dedicada y reproducida al aire.',
    icon: 'ri-music-line',
    emoji: '📻',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    pointsReward: 500,
    progressRequired: 1,
    listenerProgress: 0,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'avanzado',
  },
  {
    id: 'ach-016',
    type: 'primer_concurso',
    name: 'Ganador de Concurso',
    description: 'Ganaste un concurso RFW. ¡El esfuerzo dio frutos!',
    icon: 'ri-trophy-line',
    emoji: '🥇',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    pointsReward: 800,
    progressRequired: 1,
    listenerProgress: 0,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'avanzado',
  },
  {
    id: 'ach-017',
    type: 'primer_evento',
    name: 'Embajador de Evento',
    description: 'Invitaste a 3 amigos a un evento RFW. Sos un verdadero embajador.',
    icon: 'ri-calendar-event-line',
    emoji: '🤝',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    pointsReward: 600,
    progressRequired: 3,
    listenerProgress: 1,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'avanzado',
  },
  {
    id: 'ach-018',
    type: 'primer_club',
    name: 'Líder de Club',
    description: 'Te convertiste en uno de los miembros más activos de tu club.',
    icon: 'ri-shield-star-line',
    emoji: '👑',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    pointsReward: 800,
    progressRequired: 20,
    listenerProgress: 6,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'avanzado',
  },

  // ═══ TIER MAESTRO ═══
  {
    id: 'ach-019',
    type: 'primer_seguidor',
    name: '100 Seguidores',
    description: 'Alcanzaste los 100 seguidores. Sos una figura en la comunidad.',
    icon: 'ri-user-add-line',
    emoji: '⭐',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    pointsReward: 1000,
    progressRequired: 100,
    listenerProgress: 47,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'maestro',
  },
  {
    id: 'ach-020',
    type: 'primer_saludo',
    name: 'Saludador Frecuente',
    description: 'Enviaste 20 saludos a lo largo de tu trayectoria en RFW.',
    icon: 'ri-chat-smile-2-line',
    emoji: '🔥',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    pointsReward: 800,
    progressRequired: 20,
    listenerProgress: 12,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'maestro',
  },
  {
    id: 'ach-021',
    type: 'primera_cancion',
    name: 'DJ Honorario',
    description: 'Pediste 20 canciones. Los locutores ya conocen tu nombre.',
    icon: 'ri-music-line',
    emoji: '🎧',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    pointsReward: 800,
    progressRequired: 20,
    listenerProgress: 8,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'maestro',
  },
  {
    id: 'ach-022',
    type: 'primer_concurso',
    name: 'Rey de Concursos',
    description: 'Ganaste 3 concursos RFW. La competencia te respeta.',
    icon: 'ri-trophy-line',
    emoji: '🏅',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    pointsReward: 1500,
    progressRequired: 3,
    listenerProgress: 0,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'maestro',
  },
  {
    id: 'ach-023',
    type: 'primer_evento',
    name: 'Asistente Perfecto',
    description: 'Asististe a 10 eventos RFW. No te perdés ninguna.',
    icon: 'ri-calendar-event-line',
    emoji: '🎫',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    pointsReward: 1000,
    progressRequired: 10,
    listenerProgress: 2,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'maestro',
  },
  {
    id: 'ach-024',
    type: 'primer_club',
    name: 'Fundador de Comunidad',
    description: 'Tu actividad en el club inspiró a 10 nuevos miembros a unirse.',
    icon: 'ri-shield-star-line',
    emoji: '🏰',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    pointsReward: 1200,
    progressRequired: 10,
    listenerProgress: 3,
    unlocked: false,
    repeatable: false,
    repeatCount: 0,
    tier: 'maestro',
  },
];

export const listenerAchievementProfile: ListenerAchievementProfile = {
  listenerName: 'Marco Rivera',
  listenerAvatar: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20street%20style%20portrait%2C%20casual%20genuine%20smile%2C%20warm%20urban%20background%2C%20lifestyle%20editorial%20photography%2C%20natural%20light%2C%20approachable%20look&width=120&height=120&seq=achieve-marco-rivera&orientation=squarish',
  currentLevel: 'platino',
  currentLevelProgress: 65,
  totalAchievements: 6,
  totalScore: 1850,
  unlockedAchievements: ['ach-001', 'ach-002', 'ach-003', 'ach-004', 'ach-006', 'ach-014'],
  pendingAchievements: ['ach-005', 'ach-007', 'ach-008', 'ach-009', 'ach-010', 'ach-011', 'ach-012', 'ach-013', 'ach-015', 'ach-016', 'ach-017', 'ach-018', 'ach-019', 'ach-020', 'ach-021', 'ach-022', 'ach-023', 'ach-024'],
  nextLevel: 'diamante',
  nextLevelNeeded: 650,
  globalRank: 847,
  totalListeners: 89200,
};

export const achievementScoreActions: AchievementScoreAction[] = [
  {
    action: 'Logro Básico',
    points: 150,
    icon: 'ri-medal-line',
    color: 'text-amber-600',
    description: 'Cada logro de tier básico (Primer X) suma 100-150 puntos a tu Score RFW.',
  },
  {
    action: 'Logro Intermedio',
    points: 400,
    icon: 'ri-medal-2-line',
    color: 'text-slate-600',
    description: 'Cada logro de tier intermedio suma 250-500 puntos.',
  },
  {
    action: 'Logro Avanzado',
    points: 600,
    icon: 'ri-award-line',
    color: 'text-yellow-600',
    description: 'Cada logro avanzado suma 500-800 puntos.',
  },
  {
    action: 'Logro Maestro',
    points: 1000,
    icon: 'ri-vip-crown-line',
    color: 'text-indigo-600',
    description: 'Cada logro maestro suma 800-1500 puntos.',
  },
  {
    action: 'Subir de Nivel',
    points: 250,
    icon: 'ri-arrow-up-circle-line',
    color: 'text-emerald-600',
    description: 'Bonus de 250 puntos cada vez que subís de nivel (Bronce → Plata → Oro → Platino → Diamante).',
  },
  {
    action: 'Completar Todos los Logros',
    points: 2000,
    icon: 'ri-star-line',
    color: 'text-rose-600',
    description: 'Bonus de 2000 puntos al completar los 24 logros del sistema.',
  },
];

export const achievementDashboardStats = {
  totalAchievements: 24,
  totalListenersWithAchievements: 67200,
  totalUnlockedAchievements: 284500,
  avgAchievementsPerListener: 4.2,
  listenersBronce: 42100,
  listenersPlata: 18600,
  listenersOro: 5200,
  listenersPlatino: 1100,
  listenersDiamante: 200,
  totalScoreGenerated: 8472000,
  mostCommonAchievement: 'Primer Seguidor' as const,
  rarestAchievement: 'Fundador de Comunidad' as const,
};

// ─── HELPERS ──────────────────────────────────────────────

export function getAllAchievements(): Achievement[] {
  return allAchievements;
}

export function getUnlockedAchievements(): Achievement[] {
  return allAchievements.filter((a) => a.unlocked);
}

export function getPendingAchievements(): Achievement[] {
  return allAchievements.filter((a) => !a.unlocked);
}

export function getAchievementsByType(type: AchievementType | 'todos'): Achievement[] {
  if (type === 'todos') return allAchievements;
  return allAchievements.filter((a) => a.type === type);
}

export function getAchievementsByTier(tier: Achievement['tier'] | 'todos'): Achievement[] {
  if (tier === 'todos') return allAchievements;
  return allAchievements.filter((a) => a.tier === tier);
}

export function getAchievementById(id: string): Achievement | undefined {
  return allAchievements.find((a) => a.id === id);
}

export function getLevelConfig(level: AchievementLevel): LevelConfig | undefined {
  return levelConfigs.find((l) => l.level === level);
}

export function getLevelFromAchievements(unlockedCount: number, score: number): AchievementLevel {
  const eligible = [...levelConfigs]
    .reverse()
    .find((l) => unlockedCount >= l.minAchievements && score >= l.minScore);
  return eligible?.level || 'bronce';
}

export function getNextLevel(current: AchievementLevel): LevelConfig | null {
  const idx = levelConfigs.findIndex((l) => l.level === current);
  if (idx < levelConfigs.length - 1) return levelConfigs[idx + 1];
  return null;
}

export function formatAchievementCount(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export function formatAchievementDate(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function getProgressPercent(achievement: Achievement): number {
  return Math.min(100, Math.round((achievement.listenerProgress / achievement.progressRequired) * 100));
}